import { addClassNamesToElement, isHTMLElement } from "@lexical/utils"
import {
  $applyNodeReplacement,
  $getNearestNodeFromDOMNode,
  createCommand,
  ElementNode,
} from "lexical"
import { $isTableRowNode } from "./TableRowNode"
import { $isTableCellNode } from "./TableCellNode"
import { getTable } from "./lexicalSelectionHelpers"

/** @noInheritDoc */

export const INSERT_TABLE_COMMAND = createCommand("INSERT_TABLE_COMMAND")

export class TableNode extends ElementNode {
  static getType() {
    return "table"
  }

  static clone(node) {
    return new TableNode(node.__key)
  }

  static importDOM() {
    return {
      table: (_node) => ({
        conversion: $convertTableElement,
        priority: 1,
      }),
    }
  }

  static importJSON(_serializedNode) {
    return $createTableNode()
  }

  constructor(key) {
    super(key)
  }

  exportJSON() {
    return {
      ...super.exportJSON(),
      type: "table",
      version: 1,
    }
  }

  createDOM(config, editor) {
    const tableElement = document.createElement("table")

    addClassNamesToElement(tableElement, config.theme.table)

    return tableElement
  }

  updateDOM() {
    return false
  }

  exportDOM(editor) {
    return {
      ...super.exportDOM(editor),
      after: (tableElement) => {
        if (tableElement) {
          const newElement = tableElement.cloneNode()
          const colGroup = document.createElement("colgroup")
          const tBody = document.createElement("tbody")
          if (isHTMLElement(tableElement)) {
            tBody.append(...tableElement.children)
          }
          const firstRow = this.getFirstChildOrThrow()

          if (!$isTableRowNode(firstRow)) {
            throw new Error("Expected to find row node.")
          }

          const colCount = firstRow.getChildrenSize()

          for (let i = 0; i < colCount; i++) {
            const col = document.createElement("col")
            colGroup.append(col)
          }

          newElement.replaceChildren(colGroup, tBody)

          return newElement
        }
      },
    }
  }

  canBeEmpty() {
    return false
  }

  isShadowRoot() {
    return true
  }

  getCordsFromCellNode(tableCellNode, table) {
    const { rows, domRows } = table

    for (let y = 0; y < rows; y++) {
      const row = domRows[y]

      if (row == null) {
        continue
      }

      const x = row.findIndex((cell) => {
        if (!cell?.elem) {
          return null
        }
        const { elem } = cell
        const cellNode = $getNearestNodeFromDOMNode(elem)
        return cellNode === tableCellNode
      })

      if (x !== -1) {
        return { x, y }
      }
    }

    throw new Error("Cell not found in table.")
  }

  getDOMCellFromCords(x, y, table) {
    const { domRows } = table

    const row = domRows[y]

    if (row == null) {
      return null
    }

    const index = x < row.length ? x : row.length - 1

    const cell = row[index]

    if (cell == null) {
      return null
    }

    return cell
  }

  getDOMCellFromCordsOrThrow(x, y, table) {
    const cell = this.getDOMCellFromCords(x, y, table)

    if (!cell) {
      throw new Error("Cell not found at cords.")
    }

    return cell
  }

  getCellNodeFromCords(x, y, table) {
    const cell = this.getDOMCellFromCords(x, y, table)

    if (cell == null) {
      return null
    }

    const node = $getNearestNodeFromDOMNode(cell.elem)

    if ($isTableCellNode(node)) {
      return node
    }

    return null
  }

  getCellNodeFromCordsOrThrow(x, y, table) {
    const node = this.getCellNodeFromCords(x, y, table)

    if (!node) {
      throw new Error("Node at cords not TableCellNode.")
    }

    return node
  }

  canSelectBefore() {
    return true
  }

  canIndent() {
    return true
  }
}

export function $getElementForTableNode(editor, tableNode) {
  const tableElement = editor.getElementByKey(tableNode.getKey())

  if (tableElement == null) {
    throw new Error("Table Element Not Found")
  }

  return getTable(tableElement)
}

export function $convertTableElement(_domNode) {
  return { node: $createTableNode() }
}

export function $createTableNode() {
  return $applyNodeReplacement(new TableNode())
}

export function $isTableNode(node) {
  return node instanceof TableNode
}
